/**
 * Australian Business Number (ABN) validation and formatting utilities
 * ABN validation uses the Modulus 89 algorithm as specified by the ATO
 */

/**
 * Validates an Australian Business Number using the Modulus 89 algorithm
 * @param abn - The ABN to validate (can include spaces)
 * @returns true if the ABN is valid
 */
export function validateABN(abn: string): boolean {
  // Remove all spaces and non-digit characters
  const cleanABN = abn.replace(/\s/g, '');

  // Must be exactly 11 digits
  if (!/^\d{11}$/.test(cleanABN)) {
    return false;
  }

  // ABN weighting factors
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];

  // Convert to array of digits
  const digits = cleanABN.split('').map(Number);

  // Subtract 1 from the first digit (as per ATO algorithm)
  digits[0] -= 1;

  // Calculate weighted sum
  const sum = digits.reduce(
    (acc, digit, index) => acc + digit * weights[index],
    0
  );

  // Valid if sum is divisible by 89
  return sum % 89 === 0;
}

/**
 * Formats an ABN with standard spacing: XX XXX XXX XXX
 * @param abn - The raw ABN input
 * @returns Formatted ABN string
 */
export function formatABN(abn: string): string {
  // Remove all non-digit characters
  const cleanABN = abn.replace(/\D/g, '').slice(0, 11);

  // Apply formatting based on length
  if (cleanABN.length <= 2) {
    return cleanABN;
  } else if (cleanABN.length <= 5) {
    return `${cleanABN.slice(0, 2)} ${cleanABN.slice(2)}`;
  } else if (cleanABN.length <= 8) {
    return `${cleanABN.slice(0, 2)} ${cleanABN.slice(2, 5)} ${cleanABN.slice(5)}`;
  } else {
    return `${cleanABN.slice(0, 2)} ${cleanABN.slice(2, 5)} ${cleanABN.slice(5, 8)} ${cleanABN.slice(8)}`;
  }
}
